import PageMeta from "components/PageMeta";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { DashboardConfig, DashboardWidgetInstance, DashboardWidgetType } from "models/Dashboard";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import GridLayout, { Layout, useContainerWidth, verticalCompactor } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Alert, Button, Card, CardBody, Container, Spinner } from "reactstrap";
import * as api from "services/Api";
import { apiErrorMessage } from "services/ApiError";
import DashboardAddWidgetModal from "./DashboardAddWidgetModal";
import DashboardDataProvider from "./DashboardData";
import DashboardWidgetControls from "./DashboardWidgetControls";
import DashboardWidgetSettingsModal from "./DashboardWidgetSettingsModal";
import {
    applyVisibleLayout,
    dashboardColumnsForWidth,
    nextDashboardPosition,
    normalizeDashboardPositions,
    projectDashboardLayout,
} from "./dashboardLayout";
import { dashboardWidgetByType, settingsWithDefaults } from "./widgetRegistry";
import "./dashboard.scss";

const cloneDashboard = (dashboard: DashboardConfig): DashboardConfig => JSON.parse(JSON.stringify(dashboard));

const Dashboard: React.FC = () => {
    const { t } = useOfficeTranslation();
    const [savedDashboard, setSavedDashboard] = useState<DashboardConfig | null>(null);
    const [draftDashboard, setDraftDashboard] = useState<DashboardConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [settingsWidgetID, setSettingsWidgetID] = useState<string | null>(null);
    const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });
    const columns = dashboardColumnsForWidth(width);

    useEffect(() => {
        let active = true;
        api.getUser()
            .then(user => {
                if (!active) return;
                if (!user.dashboard) throw new Error("dashboard configuration missing");
                setSavedDashboard(cloneDashboard(user.dashboard));
                setDraftDashboard(cloneDashboard(user.dashboard));
            })
            .catch(() => { if (active) setError(t("dashboard.editor.loadFailed")); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [t]);

    const dashboard = editing ? draftDashboard : savedDashboard;
    const layout = useMemo(() => dashboard ? projectDashboardLayout(
        dashboard.widgets,
        columns,
        widget => dashboardWidgetByType.get(widget.type) ?? { minimumWidth: () => 1, minimumHeight: 2 },
    ) : [], [columns, dashboard]);
    const widgetTypes = useMemo(() => dashboard?.widgets.map(widget => widget.type) ?? [], [dashboard]);
    const selectedWidget = draftDashboard?.widgets.find(widget => widget.id === settingsWidgetID) ?? null;
    const selectedDefinition = selectedWidget ? dashboardWidgetByType.get(selectedWidget.type) ?? null : null;

    const changeDraft = useCallback((change: (current: DashboardConfig) => DashboardConfig) => {
        setDraftDashboard(current => current ? change(current) : current);
        setDirty(true);
        setError(null);
    }, []);

    const applyGridChange = useCallback((nextLayout: Layout, resizedWidgetID?: string) => {
        if (!editing) return;
        changeDraft(current => ({
            ...current,
            widgets: applyVisibleLayout(current.widgets, nextLayout, columns, resizedWidgetID),
        }));
    }, [changeDraft, columns, editing]);

    const startEditing = () => {
        if (!savedDashboard) return;
        setDraftDashboard(cloneDashboard(savedDashboard));
        setDirty(false);
        setError(null);
        setEditing(true);
    };

    const cancelEditing = () => {
        if (savedDashboard) setDraftDashboard(cloneDashboard(savedDashboard));
        setDirty(false);
        setError(null);
        setEditing(false);
        setAddOpen(false);
        setSettingsWidgetID(null);
    };

    const saveDashboard = () => {
        if (!draftDashboard) return;
        setSaving(true);
        setError(null);
        api.updateUserDashboard(draftDashboard)
            .then(saved => {
                setSavedDashboard(cloneDashboard(saved));
                setDraftDashboard(cloneDashboard(saved));
                setDirty(false);
                setEditing(false);
            })
            .catch(reason => setError(apiErrorMessage(reason, t("dashboard.editor.saveFailed"))))
            .finally(() => setSaving(false));
    };

    const addWidget = (type: DashboardWidgetType) => {
        const definition = dashboardWidgetByType.get(type);
        if (!definition) return;
        const instance: DashboardWidgetInstance = {
            id: window.crypto.randomUUID(),
            type,
            settings: settingsWithDefaults(definition),
            position: nextDashboardPosition(
                draftDashboard?.widgets ?? [],
                definition.defaultPosition.width,
                definition.defaultPosition.height,
            ),
        };
        changeDraft(current => ({ ...current, widgets: [...current.widgets, instance] }));
        setAddOpen(false);
        if (definition.settings.length > 0) setSettingsWidgetID(instance.id);
    };

    const removeWidget = (id: string) => changeDraft(current => ({
        ...current,
        widgets: current.widgets.filter(widget => widget.id !== id),
    }));

    const duplicateWidget = (widget: DashboardWidgetInstance) => {
        const duplicate = cloneDashboard({ version: 1, widgets: [widget] }).widgets[0];
        duplicate.id = window.crypto.randomUUID();
        duplicate.position = nextDashboardPosition(draftDashboard?.widgets ?? [], widget.position.width, widget.position.height);
        changeDraft(current => ({ ...current, widgets: [...current.widgets, duplicate] }));
    };

    const updateWidget = (updated: DashboardWidgetInstance) => changeDraft(current => ({
        ...current,
        widgets: normalizeDashboardPositions(current.widgets.map(widget => widget.id === updated.id ? updated : widget)),
    }));

    return <div className="page-content dashboard-page">
        <PageMeta title={t("common.dashboard")} />
        <Container fluid className="dashboard-container">
            <div className="dashboard-heading">
                <div>
                    <h4 className="mb-1">{t("common.dashboard")}</h4>
                    {editing && <span className="text-muted">{t("dashboard.editor.gridColumns", { count: columns })}</span>}
                </div>
                {!editing && <Button
                    color="link"
                    className="dashboard-edit-trigger"
                    type="button"
                    onClick={startEditing}
                    disabled={!savedDashboard}
                    aria-label={t("dashboard.editor.customize")}
                    title={t("dashboard.editor.customize")}
                >
                    <i className="mdi mdi-pencil-outline" aria-hidden="true" />
                </Button>}
                {editing && <div className="dashboard-edit-actions">
                    <Button color="light" type="button" onClick={() => setAddOpen(true)}>
                        <i className="mdi mdi-plus me-1" aria-hidden="true" />{t("dashboard.editor.addWidget")}
                    </Button>
                    <Button color="light" type="button" onClick={cancelEditing} disabled={saving}>{t("common.cancel")}</Button>
                    <Button color="primary" type="button" onClick={saveDashboard} disabled={saving || !dirty}>
                        {saving ? <><Spinner size="sm" className="me-1" />{t("common.saving")}</> : t("common.save")}
                    </Button>
                </div>}
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            {loading && <div className="dashboard-loading"><Spinner color="primary" /><span>{t("dashboard.editor.loading")}</span></div>}
            {!loading && dashboard && dashboard.widgets.length === 0 && <Card className="dashboard-empty"><CardBody>
                <i className="mdi mdi-view-dashboard-outline" aria-hidden="true" />
                <h5>{t("dashboard.editor.emptyTitle")}</h5>
                <p className="text-muted">{t("dashboard.editor.emptyDescription")}</p>
                {editing && <Button color="primary" type="button" onClick={() => setAddOpen(true)}>{t("dashboard.editor.addWidget")}</Button>}
            </CardBody></Card>}
            <div ref={containerRef as React.Ref<HTMLDivElement>} className={editing ? "dashboard-grid-wrap is-editing" : "dashboard-grid-wrap"}>
                {mounted && dashboard && dashboard.widgets.length > 0 && <DashboardDataProvider widgetTypes={widgetTypes}>
                    <GridLayout
                        width={width}
                        layout={layout}
                        gridConfig={{
                            cols: columns,
                            rowHeight: 48,
                            margin: columns === 2 ? [8, 8] : [16, 16],
                            containerPadding: [0, 0],
                        }}
                        dragConfig={{
                            enabled: editing,
                            bounded: true,
                            handle: ".dashboard-widget-drag-handle",
                            cancel: "a, input, select, textarea, button:not(.dashboard-widget-drag-handle)",
                        }}
                        resizeConfig={{ enabled: editing, handles: ["se"] }}
                        compactor={verticalCompactor}
                        onDragStop={nextLayout => applyGridChange(nextLayout)}
                        onResizeStop={(nextLayout, _oldItem, newItem) => applyGridChange(nextLayout, newItem?.i)}
                    >
                        {dashboard.widgets.map(widget => {
                            const definition = dashboardWidgetByType.get(widget.type);
                            if (!definition) return null;
                            return <div className="dashboard-grid-item" key={widget.id}>
                                {editing && <DashboardWidgetControls
                                    canConfigure={definition.settings.length > 0}
                                    titleKey={definition.titleKey}
                                    onConfigure={() => setSettingsWidgetID(widget.id)}
                                    onDuplicate={() => duplicateWidget(widget)}
                                    onRemove={() => removeWidget(widget.id)}
                                />}
                                <div className="dashboard-widget-render">{definition.render(widget)}</div>
                            </div>;
                        })}
                    </GridLayout>
                </DashboardDataProvider>}
            </div>
        </Container>
        <DashboardAddWidgetModal open={addOpen} onAdd={addWidget} onClose={() => setAddOpen(false)} />
        <DashboardWidgetSettingsModal
            open={selectedWidget !== null}
            widget={selectedWidget}
            definition={selectedDefinition}
            onChange={updateWidget}
            onClose={() => setSettingsWidgetID(null)}
        />
    </div>;
};

export default Dashboard;
